/** Copyright (c) 2018 T.Rowe Price. All rights reserved. */

import { assign, cloneDeep, every, filter, isEmpty, some } from 'lodash';
import moment from 'moment';

import {
  RECEIVED_GIFT_INDICATORS,
  RECEIVED_VIDEO_ELIGIBILITIES,
  TOGGLED_PRR_CHART
} from 'state/Actions';

import { RECEIVED_USER_ENTITLEMENTS } from 'state/session/SessionActions';

import {
  LAYOUT_CHANGED,
  DESKTOP_LAYOUT,
  MOBILE_LAYOUT,
  SALESFORCE_CHECK,
  SET_RETURNING_PAM_USER
} from 'state/view/ViewActions';
import {
  RECEIVED_ACCOUNTS,
  ACCOUNT_RESTRICTION
} from 'state/account/AccountBalancesActions';
import { RECEIVED_PENDING_TRANSACTIONS } from 'state/transaction/TransactionActions';
import { minInceptionDate } from 'services/Calculation';
import { RECEIVED_ACCOUNT_PERFORMANCE } from 'state/account/AccountPerformanceActions';
import {
  collegeSavingsViewOnlyMap,
  isCollegeSavings,
  isTracPlan
} from 'services/Products';
import { BROKERAGE_ADVANTAGE_SERVICE_ID } from 'services/Accounts';
import { DATE_DATA_FORMAT } from 'services/Dates';
import { isIE, isEdge } from 'support/LayoutUtils';
import { isIFrame } from 'support/BrowserUtils';

export const RETAIL_ENTITLEMENT = 'TRP_RET_EXTERNAL_USER_ACCESS';
export const RPS_ENTITLEMENT = 'TRP_RPS_EXTERNAL_USER_ACCESS';
export const PAM_ENTITLEMENT = 'TRP_GPS_USER_ACCESS';
export const CWV_ENTITLEMENT = 'su-admins';
export const SALESFORCE_ENTITLEMENT = 'portal_retaildesktop_base';
export const THEME_ALASKA529 = 'theme-alaska529 theme-529-only';
export const THEME_DEFAULT = 'theme-default';
export const THEME_MARYLAND529 = 'theme-maryland529 theme-529-only';
export const THEME_NATIONAL529 = 'theme-national529 theme-529-only';
export const THEME_LOADING = 'theme-loading';

export const viewInitialState = {
  isDesktopLayout: true,
  isMobileLayout: false,
  isPAM: false,
  isReturningPAM: false,
  isRPSOnly: false,
  isSalesforceRep: false,
  maxInitialRowsToShow: 10,
  hasActivePlus: false,
  hasActiveAdvisory: false,
  hasActiveAdvisoryOnly: false,
  hasSelfManaged: false,
  hasManaged: false,
  hasSelfManagedOnly: false,
  hasManagedOnly: false,
  hasAlaska529: false,
  hasOwnedAlaska529: false,
  hasOwnedAlaska529Only: false,
  hasAlaska529GiftIndicator: false,
  hasAlaska529Only: false,
  hasBrokerage: false,
  hasBrokerageOnly: false,
  hasCollegeSavings: false,
  hasEsaOnly: false,
  hasNoAccounts: false,
  hasMaryland529: false,
  hasOwnedMaryland529: false,
  hasOwnedMaryland529Only: false,
  hasMaryland529GiftIndicator: false,
  hasMaryland529Only: false,
  hasMaryland529Video: false,
  hasMutualFunds: false,
  hasNational529: false,
  hasOwnedNational529: false,
  hasNational529GiftIndicator: false,
  hasNational529Only: false,
  hasOwnedNational529Only: false,
  has529AppOnly: false,
  hasNational529Video: false,
  hasOwnedActiveAdvisoryService: false,
  hasOwnedActivePlus: false,
  isBrokerageOwner: false,
  hasOwnedCollegeSavings: false,
  hasOwnedCollegeSavingsOnly: false,
  hasOwnedMutualFunds: false,
  hasRelatedBrokerage: false,
  hasRelatedMutualFund: false,
  hasRelatedActivePlus: false,
  hasRelatedCollegeSavings: false,
  hasPendingTransactions: false,
  oldestInceptionDate: moment.utc().format(),
  hasRetirementAccounts: false,
  hasNonRetirementAccounts: false,
  hasRetirementAccountsOnly: false,
  hasNonRetirementAccountsOnly: false,
  hasWorkplaceRetirementAccounts: false,
  hasExcessAccounts: false,
  isTracOnly: false,
  hasTracAccount: false,
  prrChart: 'product',
  themeName: 'theme-loading',
  isIE: isIE,
  isEdge: isEdge
};

export const viewReducer = (state = viewInitialState, action, fullState) => {
  if (action.type === LAYOUT_CHANGED) {
    const view = cloneDeep(state);

    view['isDesktopLayout'] = action.layout === DESKTOP_LAYOUT;
    view['isMobileLayout'] = action.layout === MOBILE_LAYOUT;

    return view;
  }

  if (action.type === SALESFORCE_CHECK) {
    const view = cloneDeep(state);

    view['isSalesforceRep'] = isIFrame();

    return view;
  }

  if (action.type === RECEIVED_ACCOUNTS) {
    const view = cloneDeep(state);
    const { rpsAccounts, accounts } = action;

    // exclude purged accounts when determining available product types/flags
    const filteredAccounts = filter(accounts, (a) => !a.purgedAccount);

    const hasAccounts = accounts.length > 0;
    const hasFilteredAccounts = filteredAccounts.length > 0;
    const filteredRpsAccounts = filter(rpsAccounts, (a) => !a.purgedAccount);
    const hasRpsAccounts = filteredRpsAccounts.length > 0;

    view['hasNoAccounts'] = !hasAccounts;
    view['hasActivePlus'] = some(
      filteredAccounts,
      (a) => a.productType === fullState.products.providers.activePlus
    );
    view['hasActivePlusOnly'] =
      hasFilteredAccounts &&
      every(
        filteredAccounts,
        (a) => a.productType === fullState.products.providers.activePlus
      );
    view['hasAlaska529'] = some(
      filteredAccounts,
      (a) => a.productType === fullState.products.providers.alaska529
    );
    view['hasAlaska529Only'] =
      hasFilteredAccounts &&
      every(
        filteredAccounts,
        (a) => a.productType === fullState.products.providers.alaska529
      );
    view['hasBrokerage'] = some(
      filteredAccounts,
      (a) => a.productType === fullState.products.providers.brokerage
    );
    view['hasBrokerageOnly'] =
      hasFilteredAccounts &&
      every(
        filteredAccounts,
        (a) => a.productType === fullState.products.providers.brokerage
      );
    view['hasCollegeSavings'] = some(filteredAccounts, (a) =>
      isCollegeSavings(a.productType)
    );
    view['hasCollegeSavingsOnly'] =
      hasFilteredAccounts &&
      every(filteredAccounts, (a) => isCollegeSavings(a.productType));
    view['hasMaryland529'] = some(
      filteredAccounts,
      (a) => a.productType === fullState.products.providers.maryland529
    );
    view['hasMaryland529Only'] =
      hasFilteredAccounts &&
      every(
        filteredAccounts,
        (a) => a.productType === fullState.products.providers.maryland529
      );
    view['hasMutualFunds'] = some(
      filteredAccounts,
      (a) => a.productType === fullState.products.providers.mutualFund
    );
    view['hasMutualFundsOnly'] =
      hasFilteredAccounts &&
      every(
        filteredAccounts,
        (a) => a.productType === fullState.products.providers.mutualFund
      );
    view['hasNational529'] = some(
      filteredAccounts,
      (a) => a.productType === fullState.products.providers.national529
    );
    view['hasNational529Only'] =
      hasFilteredAccounts &&
      every(
        filteredAccounts,
        (a) => a.productType === fullState.products.providers.national529
      );
    //DEPRECATED: Don't use this. Use hasOwnedNational529
    view['hasOwnedNational529Only'] =
      hasFilteredAccounts &&
      every(
        filteredAccounts,
        (a) =>
          a.owner && a.productType === fullState.products.providers.national529
      );
    //DEPRECATED: Don't use this. Use hasOwnedMaryland529Only
    view['hasOwnedAlaska529Only'] =
      hasFilteredAccounts &&
      every(
        filteredAccounts,
        (a) =>
          a.owner && a.productType === fullState.products.providers.alaska529
      );
    //DEPRECATED: Don't use this. Use hasOwnedAlaska529Only
    view['hasOwnedMaryland529Only'] =
      hasFilteredAccounts &&
      every(
        filteredAccounts,
        (a) =>
          a.owner && a.productType === fullState.products.providers.maryland529
      );
    view['has529AppOnly'] =
      hasFilteredAccounts &&
      every(
        filteredAccounts,
        (a) =>
          isCollegeSavings(a.productType) ||
          a.productType === fullState.products.providers.activePlus
      );
    view['has529Only'] =
      view.hasAlaska529Only ||
      view.hasMaryland529Only ||
      view.hasNational529Only;
    view['hasActiveAdvisory'] = some(
      filteredAccounts,
      (a) => a.productType === fullState.products.providers.activeAdvisory
    );
    view['hasActiveAdvisoryOnly'] =
      hasFilteredAccounts &&
      every(
        filteredAccounts,
        (a) => a.productType === fullState.products.providers.activeAdvisory
      );
    view['hasSelfManaged'] = some(
      filteredAccounts,
      (a) =>
        a.productType !== fullState.products.providers.activeAdvisory &&
        a.productType !== fullState.products.providers.activePlus
    );

    view['hasOwnedActivePlus'] =
      view.hasActivePlus &&
      some(
        filteredAccounts,
        (a) =>
          a.owner && a.productType === fullState.products.providers.activePlus
      );
    view['hasOwnedAlaska529'] =
      view.hasAlaska529 &&
      some(
        filteredAccounts,
        (a) =>
          a.owner && a.productType === fullState.products.providers.alaska529
      );
    view['hasOwnedMaryland529'] =
      view.hasMaryland529 &&
      some(
        filteredAccounts,
        (a) =>
          a.owner && a.productType === fullState.products.providers.maryland529
      );
    view['hasOwnedNational529'] =
      view.hasNational529 &&
      some(
        filteredAccounts,
        (a) =>
          a.owner && a.productType === fullState.products.providers.national529
      );
    view['isBrokerageOwner'] =
      view.hasBrokerage &&
      some(
        filteredAccounts,
        (a) =>
          a.owner && a.productType === fullState.products.providers.brokerage
      );
    view['hasBrokerageAdvantageOnly'] = every(
      filteredAccounts,
      (a) =>
        a.serviceIds &&
        a.serviceIds.every(
          (serviceId) => serviceId === BROKERAGE_ADVANTAGE_SERVICE_ID
        )
    );
    view['hasOwnedCollegeSavings'] =
      view.hasCollegeSavings &&
      some(filteredAccounts, (a) => a.owner && isCollegeSavings(a.productType));
    view['hasOwnedMutualFunds'] =
      view.hasMutualFunds &&
      some(
        filteredAccounts,
        (a) =>
          a.owner && a.productType === fullState.products.providers.mutualFund
      );
    view['hasOwnedActiveAdvisory'] =
      view.hasActiveAdvisory &&
      some(
        filteredAccounts,
        (a) =>
          a.owner &&
          a.productType === fullState.products.providers.activeAdvisory
      );

    view['hasRelatedBrokerage'] =
      view.hasBrokerage &&
      some(
        filteredAccounts,
        (a) =>
          a.isRelated &&
          a.productType === fullState.products.providers.brokerage
      );
    view['hasRelatedMutualFund'] =
      view.hasMutualFunds &&
      some(
        filteredAccounts,
        (a) =>
          a.isRelated &&
          a.productType === fullState.products.providers.mutualFund
      );
    view['hasRelatedActivePlus'] =
      view.hasActivePlus &&
      some(
        filteredAccounts,
        (a) =>
          a.isRelated &&
          a.productType === fullState.products.providers.activePlus
      );
    view['hasRelatedCollegeSavings'] =
      view.hasCollegeSavings &&
      some(
        filteredAccounts,
        (a) => a.isRelated && isCollegeSavings(a.productType)
      );
    view['hasRelatedActiveAdvisory'] =
      view.hasActiveAdvisory &&
      some(
        filteredAccounts,
        (a) =>
          a.isRelated &&
          a.productType === fullState.products.providers.activeAdvisory
      );

    view['hasRetirementAccounts'] = some(
      filteredAccounts,
      (a) => a.retirementAccount
    );
    view['hasNonRetirementAccounts'] = some(
      filteredAccounts,
      (a) => !a.retirementAccount
    );
    view['hasRetirementAccountsOnly'] =
      hasFilteredAccounts &&
      every(filteredAccounts, (a) => a.retirementAccount);
    view['hasNonRetirementAccountsOnly'] =
      hasFilteredAccounts &&
      every(filteredAccounts, (a) => !a.retirementAccount);
    view['hasEsaOnly'] =
      hasAccounts && every(filteredAccounts, (a) => a.accountTypeId === 9);

    view['hasWorkplaceRetirementAccounts'] = hasRpsAccounts;
    view['isTracOnly'] =
      !hasFilteredAccounts &&
      hasRpsAccounts &&
      every(rpsAccounts, (a) => isTracPlan(a.productType));
    view['hasTracAccount'] =
      hasRpsAccounts && some(rpsAccounts, (a) => isTracPlan(a.productType));
    view['hasWorkplaceRetirementAccountsOnly'] = !hasAccounts && hasRpsAccounts;
    view['hasManaged'] = view.hasActivePlus || view.hasActiveAdvisory;
    view['hasSelfManagedOnly'] = view.hasSelfManaged && !view.hasManaged;
    view['hasManagedOnly'] = view.hasManaged && !view.hasSelfManaged;

    // reduce 529 product types to determine view only booleans
    view['viewOnly529'] = collegeSavingsViewOnlyMap(accounts);

    const { hasAlaska529Only, hasMaryland529Only, hasNational529Only } = view;
    if (hasAccounts) {
      //if you have no accounts, don't update the theme name, leave the theme as theme-loading
      view['themeName'] = hasAlaska529Only
        ? THEME_ALASKA529
        : hasMaryland529Only
          ? THEME_MARYLAND529
          : hasNational529Only
            ? THEME_NATIONAL529
            : THEME_DEFAULT;
    }

    return view;
  }

  if (action.type === ACCOUNT_RESTRICTION) {
    const view = cloneDeep(state);
    view['hasExcessAccounts'] = true;
    return view;
  }

  if (action.type === RECEIVED_GIFT_INDICATORS) {
    const view = cloneDeep(state);

    view['hasNational529GiftIndicator'] = some(
      action.giftingindicators,
      (a) =>
        a.productType === fullState.products.providers.national529 &&
        a.giftingEnabled
    );
    view['hasAlaska529GiftIndicator'] = some(
      action.giftingindicators,
      (a) =>
        a.productType === fullState.products.providers.alaska529 &&
        a.giftingEnabled
    );
    view['hasMaryland529GiftIndicator'] = some(
      action.giftingindicators,
      (a) =>
        a.productType === fullState.products.providers.maryland529 &&
        a.giftingEnabled
    );

    return view;
  }

  if (action.type === RECEIVED_USER_ENTITLEMENTS) {
    const view = cloneDeep(state);
    view['isPAM'] = some(
      action.entitlements,
      (entitlement) => entitlement === PAM_ENTITLEMENT
    );
    return view;
  }

  if (action.type === RECEIVED_USER_ENTITLEMENTS) {
    const view = cloneDeep(state);
    const userEntitlements = action.entitlements;
    const retailClient = some(
      userEntitlements,
      (entitlement) => entitlement === RETAIL_ENTITLEMENT
    );
    const rpsClient = some(
      userEntitlements,
      (entitlement) => entitlement === RPS_ENTITLEMENT
    );
    view['isRPSOnly'] = Boolean(rpsClient && !retailClient);
    return view;
  }

  if (action.type === RECEIVED_VIDEO_ELIGIBILITIES) {
    const view = cloneDeep(state);

    view['hasNational529Video'] = some(
      action.videoeligibilities,
      (a) =>
        a.productType === fullState.products.providers.national529 &&
        a.isEligible
    );
    view['hasMaryland529Video'] = some(
      action.videoeligibilities,
      (a) =>
        a.productType === fullState.products.providers.maryland529 &&
        a.isEligible
    );

    return view;
  }

  if (action.type === TOGGLED_PRR_CHART) {
    return assign({}, state, { prrChart: action.prrChart });
  }

  if (action.type === RECEIVED_PENDING_TRANSACTIONS) {
    const view = cloneDeep(state);
    view['hasPendingTransactions'] = !isEmpty(action.transactions);
    return view;
  }

  if (action.type === RECEIVED_ACCOUNT_PERFORMANCE) {
    const view = cloneDeep(state);
    const inceptionDate = minInceptionDate(action.accountPerformance);
    view['oldestInceptionDate'] = inceptionDate
      ? moment.utc(inceptionDate).format(DATE_DATA_FORMAT)
      : null;
    return view;
  }

  if (action.type === SET_RETURNING_PAM_USER) {
    const view = cloneDeep(state);
    view['isReturningPAM'] = action;
    return view;
  }

  return state;
};
viewReducer.priority = 11;